import * as React from "react";
import { Box, CircularProgress, Link, TextField, Typography } from "@mui/material";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import axios from "axios";
import { authHeader, getToken, logout } from "../../authClient";
import { recoverWithCode, addPasskey, formatCodeInput, browserSupportsWebAuthn } from "../../auth/passkeys";
import { AuthLayout, Eyebrow, Heading, Notice, PrimaryButton, LineButton, fieldSx, linkSx } from "../authShared";
import { RecoveryCodeView } from "./RecoveryCodeView";

// ─────────────────────────────────────────────────────────────────────────────
// Recover with the recovery code.
//
//   1. Code     — opens a recovery session (the code is spent at that moment).
//   2. Passkey  — mandatory, within 15 minutes; nothing else works until then.
//   3. New code — the old one is gone; this is its replacement, shown once.
//
// Said plainly up front: for 24 hours stream keys and destinations stay locked,
// and the account's other devices are asked "was this you?". That is what makes
// a stolen recovery code much less useful, so it is explained, not hidden.
// ─────────────────────────────────────────────────────────────────────────────

const timeOf = (d) => (d ? new Date(d).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "");

export default function Recover() {
  const navigate = useNavigate();
  const supported = React.useMemo(() => browserSupportsWebAuthn(), []);
  const [step, setStep] = React.useState("code"); // code → passkey → newcode
  const [input, setInput] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const [deadline, setDeadline] = React.useState(null);
  const [restrictedUntil, setRestrictedUntil] = React.useState(null);
  const [newCode, setNewCode] = React.useState("");

  // Arriving here with a recovery session that still owes its passkey (sent by
  // authClient on 403 passkey_required, or a reload mid-way): resume at step 2.
  React.useEffect(() => {
    if (!getToken()) return;
    axios.get("/api/me", { headers: authHeader(), __noAuthRedirect: true })
      .then(({ data }) => {
        if (data?.session?.mustRegisterPasskey) {
          setDeadline(data.session.passkeyDeadline);
          setRestrictedUntil(data.session.restrictedUntil);
          setStep("passkey");
        }
      })
      .catch(() => {});
  }, []);

  const submitCode = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const data = await recoverWithCode(input);
      setDeadline(data.passkeyDeadline);
      setRestrictedUntil(data.restrictedUntil);
      setInput("");
      setStep("passkey");
    } catch (err) {
      setError(err.message);
    } finally { setBusy(false); }
  };

  const createPasskey = async () => {
    setBusy(true); setError("");
    try {
      const data = await addPasskey();
      setNewCode(data.recoveryCode || "");
      setStep("newcode");
    } catch (err) {
      if (!err.cancelled) setError(err.message);
    } finally { setBusy(false); }
  };

  const giveUp = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <AuthLayout
      maxWidth={460}
      panel={{
        eyebrow: "Account recovery",
        heading: "Back in, safely.",
        body: "A recovery code gets you into your account on a new device. To protect you if someone else ever gets hold of it, stream keys and destinations stay locked for 24 hours, and your other devices can cancel the recovery.",
      }}
    >
      {step === "code" && (
        <>
          <Eyebrow>Recovery</Eyebrow>
          <Heading>Use your recovery code</Heading>
          <Typography sx={{ color: "var(--fn-muted-l)", fontSize: 15.5, lineHeight: 1.55, mb: 3 }}>
            Enter the 24-character code you saved when you created your account. It works once; you'll get a new
            one right after.
          </Typography>
          {error && <Notice onClose={() => setError("")}>{error}</Notice>}
          <Box component="form" onSubmit={submitCode} noValidate>
            <TextField
              fullWidth autoFocus label="Recovery code" value={input}
              onChange={(e) => setInput(formatCodeInput(e.target.value))}
              placeholder="XXXX-XXXX-XXXX-XXXX-XXXX-XXXX"
              autoComplete="off" spellCheck={false} disabled={busy}
              inputProps={{ "aria-describedby": "code-help", style: { fontFamily: "var(--fn-mono)", letterSpacing: ".06em" } }}
              sx={{ ...fieldSx, mb: 1 }}
            />
            <Typography id="code-help" sx={{ fontSize: 13, color: "var(--fn-muted-l)", mb: 2.5 }}>
              Dashes and capitals don't matter.
            </Typography>
            <PrimaryButton type="submit" disabled={busy || input.replace(/-/g, "").length !== 24}>
              {busy ? <CircularProgress size={21} thickness={5} sx={{ color: "var(--fn-cream)" }} /> : "Continue"}
            </PrimaryButton>
          </Box>
          <Typography sx={{ mt: 2.5, fontSize: 14.5, color: "var(--fn-muted-l)", textAlign: "center" }}>
            Found your passkey?{" "}
            <Link component={RouterLink} to="/login" sx={linkSx}>Sign in</Link>
          </Typography>
        </>
      )}

      {step === "passkey" && (
        <>
          <Eyebrow>Step 2 of 3</Eyebrow>
          <Heading>Create a new passkey</Heading>
          <Typography sx={{ color: "var(--fn-muted-l)", fontSize: 15.5, lineHeight: 1.55, mb: 2.5 }}>
            Your code was accepted. Create a passkey on this device to finish
            {deadline ? <> — before <b>{timeOf(deadline)}</b></> : null}.
          </Typography>
          <Box sx={{ p: 2, mb: 3, borderRadius: "12px", bgcolor: "rgba(244,183,64,.12)", color: "#7A5410", fontSize: 14, lineHeight: 1.55 }}>
            For your safety, until <b>{restrictedUntil ? new Date(restrictedUntil).toLocaleString([], { weekday: "short", hour: "2-digit", minute: "2-digit" }) : "24 hours from now"}</b> you
            won't be able to see or change stream keys and destinations, or delete the account. Your other devices are
            being told about this recovery.
          </Box>
          {error && <Notice onClose={() => setError("")}>{error}</Notice>}
          <PrimaryButton onClick={createPasskey} disabled={busy || !supported} startIcon={!busy && <FingerprintIcon />}>
            {busy ? <CircularProgress size={21} thickness={5} sx={{ color: "var(--fn-cream)" }} /> : "Create my passkey"}
          </PrimaryButton>
          <LineButton onClick={giveUp} disabled={busy} sx={{ mt: 1.5 }}>Cancel and sign out</LineButton>
        </>
      )}

      {step === "newcode" && (
        <>
          <Eyebrow>Step 3 of 3</Eyebrow>
          <Box sx={{ mt: 2.5 }}>
            <RecoveryCodeView
              code={newCode}
              title="Your new recovery code"
              intro="The code you just used no longer works. This is its replacement. It's shown only now."
              continueLabel="Go to my studio"
              onContinue={() => { setNewCode(""); navigate("/home", { replace: true }); }}
            />
          </Box>
        </>
      )}
    </AuthLayout>
  );
}
